import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
 Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { useAllSpecialties } from "@/hooks/useAllSpecialties";
import { cn } from "@/lib/utils";

export function SpecialtySelect({
  value,
  onChange,
  placeholder,
  allowCustom = true,
  icon: Icon,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const specialties = useAllSpecialties();

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? specialties.filter((s) => s.toLowerCase().includes(q))
      : specialties;
    // Cap rendered items for performance, search narrows it instantly.
    return list.slice(0, 200);
  }, [query, specialties]);

  const showCustom =
    allowCustom &&
    query.trim().length > 0 &&
    !specialties.some((s) => s.toLowerCase() === query.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-slot="select-trigger"
          className={cn(
            "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap border-0 bg-transparent px-0 py-2 text-sm shadow-none outline-none focus:ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer",
            className
          )}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {Icon && <Icon className="h-4 w-4 text-primary shrink-0" />}
            <span className={cn("truncate text-left", !value && "text-muted-foreground")}>
              {value || placeholder || "Select specialty…"}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[320px] max-w-[400px] p-0 rounded-xl border border-border/80 shadow-xl overflow-hidden bg-popover text-popover-foreground"
        align="start"
        sideOffset={8}
      >
        <Command shouldFilter={false} className="w-full bg-transparent">
          <CommandInput
            placeholder="Search specialty…"
            value={query}
            onValueChange={setQuery}
            className="border-0"
          />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">No match found.</CommandEmpty>
            <CommandGroup className="p-1.5">
              {items.map((s) => (
                <CommandItem
                  key={s}
                  value={s}
                  onSelect={() => {
                    onChange(s);
                    setOpen(false);
                    setQuery("");
                  }}
                  className="cursor-pointer py-2.5 px-3 rounded-lg text-sm font-normal aria-selected:bg-primary/10 aria-selected:text-primary transition-colors flex items-center gap-2"
                >
                  <Check className={cn("h-4 w-4 text-primary shrink-0 transition-opacity", value === s ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{s}</span>
                </CommandItem>
              ))}
              {showCustom && (
                <CommandItem
                  value={`__custom_${query}`}
                  onSelect={() => {
                    onChange(query.trim());
                    setOpen(false);
                    setQuery("");
                  }}
                  className="cursor-pointer py-2.5 px-3 rounded-lg text-sm font-normal aria-selected:bg-primary/10 aria-selected:text-primary transition-colors flex items-center gap-2"
                >
                  <Check className="h-4 w-4 opacity-0 shrink-0" />
                  <span className="truncate">Use “{query.trim()}”</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
